import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Scale, HeartHandshake, Landmark } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const RALLY =
  "https://images.unsplash.com/photo-1505148359496-35d8d1ec9645?crop=entropy&cs=srgb&fm=jpg&q=85";

const CHIPS = [
  { icon: Scale, label: "Advocate of the High Court" },
  { icon: HeartHandshake, label: "Disability-rights champion" },
  { icon: Landmark, label: "Three-term legislator" },
];

export const Portrait = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section id="about" ref={ref} data-testid="portrait-section" className="relative py-24 sm:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <Reveal className="spotlight-frame order-2 lg:order-1" data-testid="portrait-image-frame">
          <motion.img
            style={{ y: yImg }}
            src={RALLY}
            alt="Grassroots community gathering in Kenya"
            className="w-full h-[420px] sm:h-[540px] object-cover scale-110 max-h-[80vh]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10]/85 via-transparent to-[#0B0C10]/20" />
          <div className="absolute bottom-0 inset-x-0 p-6">
            <p className="mono text-[11px] tracking-[0.25em] uppercase text-orange-400">
              From Bungoma. For Bungoma.
            </p>
          </div>
        </Reveal>

        <div className="order-1 lg:order-2">
          <Reveal>
            <p className="eyebrow mb-4" data-testid="portrait-eyebrow">The Man</p>
            <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.08]">
              A statesman forged by <span className="text-orange-500">resilience.</span>
            </h2>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="mt-7 space-y-5 text-base sm:text-lg text-stone-400 leading-relaxed">
              <p>
                Born in 1963 in Bungoma County, Timothy Wanyonyi Wetangula trained as a
                lawyer — LL.B, University of Delhi, then the Kenya School of Law — and
                began practice at Wetang'ula &amp; Co. Advocates.
              </p>
              <p>
                In 1998, a carjacking in Nairobi left him with a spinal-cord injury and
                life in a wheelchair. He answered not with retreat but with service:
                co-founding the Kenya Paraplegic Organization in 2004, serving as a
                nominated Nairobi councillor in 2007, and winning the Westlands
                parliamentary seat in 2013 — a seat he has held ever since.
              </p>
              <p>
                He is the younger brother of National Assembly Speaker Moses Wetang'ula,
                and one of Kenya's most visible champions for persons with disabilities.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <blockquote
              data-testid="portrait-philosophy-quote"
              className="mt-8 border-l-2 border-orange-500 pl-6 py-1"
            >
              <p className="font-display font-semibold text-xl sm:text-2xl text-stone-100 leading-snug">
                "Service without discrimination. Integrity without compromise."
              </p>
            </blockquote>
          </Reveal>

          <Reveal delay={0.28}>
            <div className="mt-8 flex flex-wrap gap-3">
              {CHIPS.map((c) => (
                <span
                  key={c.label}
                  data-testid={`portrait-chip-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-4 py-2 mono text-[11px] tracking-[0.12em] uppercase text-stone-300"
                >
                  <c.icon size={14} className="text-orange-500" />
                  {c.label}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
